import React, { useState } from "react"
import { navigate } from "gatsby-link"
import styled from "@emotion/styled"
import { Box, Flex } from "@rebass/grid/emotion"
import theme from "@nutrafol/nutrafol-ui-kit/styles/theme"
import { TextElement } from "@nutrafol/nutrafol-ui-kit/dist/TextElement"
import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"
import { ButtonWild } from "@nutrafol/nutrafol-ui-kit/dist/ButtonWild"

import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemPanel,
  resetNextUuid,
} from "react-accessible-accordion"

import DownNav from "../../../../assets/icons/wild/arrow-down-nav.svg"

const FaqFullWrap = styled.div`
  &.faq-full-wrap {
    padding-top: 50px;
    padding-bottom: 64px;
    @media (min-width: 768px) {
      padding-top: 50px;
      padding-bottom: 80px;
    }
    @media (min-width: 1120px) {
      padding-top: 64px;
      padding-bottom: 128px;
    }
  }
  &.no-border-radius .FaqWrap,
  &.no-border-radius .c-faq {
    border-radius: 0 !important;
  }
`

const StyledAccordionItem = styled(AccordionItem)`
  padding: 0;
  border-top: 1px solid ${theme.wild.color.master.base.alt.secondary};
  h5 {
    cursor: pointer;
  }
  p:not(:last-of-type) {
    padding: 0 0 8px 0;
  }
  &:last-of-type {
    @media (min-width: 768px) {
      border-bottom: 1px solid ${theme.wild.color.master.base.alt.secondary};
    }
  }
`
const StyledAccordionItemHeading = styled.div`
  outline: none !important;
`

const StyledAccordionItemButton = styled(AccordionItemButton)`
  width: 100%;
  display: flex;
  justify-content: space-between;
  outline: none !important;
  padding: 22px 0 21px;
  @media (min-width: 768px) {
    padding: 32px 0;
  }
  svg.plus-icon {
    display: block !important;
    height: 21px;
    width: 9px;
    transition-duration: 0.4s;
    transition-property: transform;
  }

  &[aria-expanded="true"] {
    svg.plus-icon {
      transform: rotate(180deg);
    }
  }
  > .text {
    width: 95%;
    //font-size: 16px;
    color: ${theme.wild.color.master.base.black};
  }
  @media (max-width: 768px) {
    > .text {
      font-size: 14px;
    }
  }
`

const StyledAccordionItemPanel = styled(AccordionItemPanel)`
  h5 {
    padding: 8px 16px 0 0;
  }
  padding: 0 0 16px;
  @media (min-width: 768px) {
    padding: 0 0 32px;
  }
`
const FaqWrap = styled(Flex)`
  border-radius: 8px;
  margin: auto;
  padding: 0;
  flex-direction: column;
  max-width: 1120px;
  &.growthPlanPlusRadius {
    border-radius: 4px;
  }
  &.wide {
    max-width: 100%;
  }
  .c-faq {
    padding: 40px 16px 0;
    background-color: ${theme.color.beige};
    border-radius: 8px;
    order: 1;
    @media (min-width: 768px) {
      padding: 80px;
    }
    .title--large-i {
      padding: 0 0 40px 0;
      //border-bottom: 1px solid ${theme.wild.color.master.base.alt.secondary};
      @media (min-width: 768px) {
        padding: 0 0 45px 0;
      }
    }
  }
`
const SeeAllQuestionsButton = styled(ButtonWild)`
  margin-bottom: 1rem;
  transform: translateY(-150%);
`

const FaqArea = ({
  questions = [],
  isWide = false,
  hasContainer = true,
  isGrowthPlanPlus = false,
  heading = "Common questions.",
  showSeeAllQuestionsButton = false,
  isFullWrap = true,
}) => {
  const [expanded, setExpanded] = useState(false)

  // Reset uuid - https://github.com/springload/react-accessible-accordion/issues/41#issuecomment-822532383
  resetNextUuid()

  const clickHandler = (e, section) => {
    setExpanded(false)
    try {
      if (
        e.target.closest("div[aria-expanded]").getAttribute("aria-expanded") ===
        "false"
      ) {
        setExpanded(true)
        segmentEvent("Section Expanded", {
          section,
        })
      } else {
        segmentEvent("Section Collapsed", {
          section,
        })
      }
    } catch (e) {
      //error
    }
  }

  const mapAccordionItems = questions.map((question, idx) => (
    <StyledAccordionItem key={idx} uuid={`item-${idx}`}>
      <StyledAccordionItemHeading
        onClick={(e) => clickHandler(e, question.title)}
        pb={[42, 42, 42, 200]}
      >
        <StyledAccordionItemButton>
          <TextElement
            element="h4"
            text={question.title}
            className="text body--large hide-md-up"
          />
          <TextElement
            element="h4"
            text={question.title}
            className="text body--xlarge show-md-up"
          />
          <DownNav className="plus-icon" />
        </StyledAccordionItemButton>
      </StyledAccordionItemHeading>
      <StyledAccordionItemPanel>
        <div dangerouslySetInnerHTML={{ __html: question.answer }} />
      </StyledAccordionItemPanel>
    </StyledAccordionItem>
  ))

  const gpPlusRadius = isGrowthPlanPlus ? "growthPlanPlusRadius" : ""

  const SEE_ALL_QUESTIONS_LABEL = "See All FAQs"
  const handleSeeAllQuestionsClick = () => {
    segmentEvent("Marketing CTA Clicked", {
      destination: "/faq/",
      module_location: "bottom_fold",
      text: SEE_ALL_QUESTIONS_LABEL,
      type: "link",
    })
    navigate("/faq/")
  }

  return (
    <FaqFullWrap
      className={`${
        hasContainer
          ? `wild-container ${isFullWrap ? "faq-full-wrap" : "faq-no-wrap"}`
          : `${gpPlusRadius ? gpPlusRadius : "no-border-radius"}`
      }`}
    >
      <FaqWrap className={isWide ? "wide FaqWrap" : "FaqWrap"}>
        <Box className={`c-faq`} width={[1]}>
          <TextElement
            element="h3"
            text={heading}
            className={`title--large-i text-master-base-black ${
              expanded ? "expanded" : ""
            }`}
          />
          {showSeeAllQuestionsButton && (
            <SeeAllQuestionsButton
              textLink
              arrowRight
              label={SEE_ALL_QUESTIONS_LABEL}
              onClick={handleSeeAllQuestionsClick}
            />
          )}
          <Accordion allowZeroExpanded={true}>{mapAccordionItems}</Accordion>
        </Box>
      </FaqWrap>
    </FaqFullWrap>
  )
}

export default FaqArea
